import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function FinancialStatementSection({ caseId, sofaResponse }) {
  const [statements, setStatements] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newStatement, setNewStatement] = useState({
    party_name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    date_issued: ''
  });
  const [editStatement, setEditStatement] = useState(null);

  // Fetch existing financial statements
  useEffect(() => {
    if (!sofaResponse) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-financial-statements/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch statements');
        setStatements(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId, sofaResponse]);

  // Handlers
  function openAddModal() {
    setNewStatement({
      party_name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip_code: '',
      date_issued: ''
    });
    setModalOpen(true);
  }

  function openEditModal(item) {
    setEditStatement({ ...item });
    setEditModalOpen(true);
  }

  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-financial-statements/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newStatement)
        }
      );
      if (!res.ok) throw new Error();
      const created = await res.json();
      setStatements(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error('Failed to add statement', err);
    }
  }

  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-financial-statements/${editStatement.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editStatement)
        }
      );
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setStatements(prev =>
        prev.map(s => (s.id === updated.id ? updated : s))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update statement', err);
    }
  }

  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-financial-statements/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error();
      setStatements(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete statement', err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 28: Financial Statements Provided
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List persons you have given a financial statement regarding your business within the last 2 years.
        </p>
      {statements.length === 0 ? (
        <p>No financial statements recorded.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {statements.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(item.id);
                }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" 
                     className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                           2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                           6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                           2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                           0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                           00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.party_name || '—'}</h3>
              <p className="mt-1 text-sm">
                {item.street1 || ''}{item.street2 ? `, ${item.street2}` : ''}, {item.city || ''}, {item.state || ''} {item.zip_code || ''}
              </p>
              <p className="mt-2 text-sm">
                <strong>Date Issued:</strong> {item.date_issued || '—'}
              </p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Financial Statement
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Add Financial Statement</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Party Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newStatement.party_name}
                onChange={e => setNewStatement(prev => ({ ...prev, party_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <input
                type="text"
                placeholder="Street 1"
                className="w-full border rounded px-2 py-1"
                value={newStatement.street1}
                onChange={e => setNewStatement(prev => ({ ...prev, street1: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Street 2"
                className="w-full border rounded px-2 py-1"
                value={newStatement.street2}
                onChange={e => setNewStatement(prev => ({ ...prev, street2: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="City"
                  className="w-full border rounded px-2 py-1"
                  value={newStatement.city}
                  onChange={e => setNewStatement(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  placeholder="State"
                  className="w-full border rounded px-2 py-1"
                  value={newStatement.state}
                  onChange={e => setNewStatement(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Zip"
                  className="w-full border rounded px-2 py-1"
                  value={newStatement.zip_code}
                  onChange={e => setNewStatement(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Date Issued</label>
              <input
                type="date"
                className="w-full border rounded px-2 py-1"
                value={newStatement.date_issued}
                onChange={e => setNewStatement(prev => ({ ...prev, date_issued: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editStatement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Edit Financial Statement</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Party Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editStatement.party_name}
                onChange={e => setEditStatement(prev => ({ ...prev, party_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <input
                type="text"
                placeholder="Street 1"
                className="w-full border rounded px-2 py-1"
                value={editStatement.street1}
                onChange={e => setEditStatement(prev => ({ ...prev, street1: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Street 2"
                className="w-full border rounded px-2 py-1"
                value={editStatement.street2}
                onChange={e => setEditStatement(prev => ({ ...prev, street2: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="City"
                  className="w-full border rounded px-2 py-1"
                  value={editStatement.city}
                  onChange={e => setEditStatement(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  placeholder="State"
                  className="w-full border rounded px-2 py-1"
                  value={editStatement.state}
                  onChange={e => setEditStatement(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Zip"
                  className="w-full border rounded px-2 py-1"
                  value={editStatement.zip_code}
                  onChange={e => setEditStatement(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Date Issued</label>
              <input
                type="date"
                className="w-full border rounded px-2 py-1"
                value={editStatement.date_issued}
                onChange={e => setEditStatement(prev => ({ ...prev, date_issued: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
      );
}
